import { LEAVE_STATUS, leaveStatusToken } from "./leaveLookups";
import { formatLeaveRange } from "./leaveDates";

/**
 * Leave API errors in plain English.
 *
 * The backend's messages are written for developers ("Leave at index 0
 * overlaps leave 6487… (2026-10-14T18:30:00+00:00 to …)"). Every leave write
 * and read goes through `friendlyLeaveError` before its message reaches the
 * screen; anything it doesn't recognise falls back to a generic line for the
 * action rather than leaking the raw text.
 */

const OPEN = new Set([LEAVE_STATUS.PENDING, LEAVE_STATUS.APPROVED]);

/** How a status reads in a sentence ("approved", "withdrawn"). */
function statusWord(token) {
  return token === LEAVE_STATUS.CANCELLED ? "withdrawn" : token;
}

/**
 * The sentence for "these dates clash with an existing leave".
 *
 * @param {{ status?: string, start_date: string, end_date: string }} leave - The existing leave.
 * @returns {string}
 */
export function overlapMessage(leave) {
  const token = leaveStatusToken(leave?.status);
  const kind =
    token === LEAVE_STATUS.APPROVED
      ? "an approved leave"
      : token === LEAVE_STATUS.PENDING
        ? "a pending leave request"
        : "a leave request";
  return `You already have ${kind} for ${formatLeaveRange(leave)}. Choose different dates, or withdraw that request first.`;
}

/**
 * The coach's own open (pending or approved) leave that a new window would
 * overlap, if any — the same half-open rule the backend applies.
 *
 * @param {Array<Object>} leaves
 * @param {{ start: string, end: string }} window
 * @returns {Object|null}
 */
export function findOverlappingLeave(leaves, window) {
  if (!window) return null;
  const start = new Date(window.start).getTime();
  const end = new Date(window.end).getTime();
  return (
    (Array.isArray(leaves) ? leaves : []).find((l) => {
      if (!OPEN.has(leaveStatusToken(l?.status))) return false;
      return new Date(l.start_date).getTime() < end && start < new Date(l.end_date).getTime();
    }) || null
  );
}

const FALLBACKS = {
  load: "Couldn't load leave requests. Please try again.",
  apply: "Couldn't submit your leave request. Please try again.",
  withdraw: "Couldn't withdraw this request. Please try again.",
  decide: "Couldn't save this decision. Please try again.",
};

/**
 * @param {any} err - Usually an Error from `wrapCommsError` (`message` + `status`).
 * @param {Object} [options]
 * @param {'load'|'apply'|'withdraw'|'decide'} [options.action='apply']
 * @param {Array<Object>} [options.leaves] - Known leaves, so an overlap can
 *   name the existing leave's status.
 * @returns {string}
 */
export function friendlyLeaveError(err, { action = "apply", leaves = [] } = {}) {
  const raw = String(err?.message || "");
  const status = err?.status;

  const overlap = /overlaps leave ([0-9a-f-]{36}) \((\S+) to (\S+)\)/i.exec(raw);
  if (overlap) {
    const known = (leaves || []).find((l) => String(l.id) === overlap[1]);
    return overlapMessage(known || { start_date: overlap[2], end_date: overlap[3] });
  }
  if (/Leaves at index \d+ and \d+ overlap/i.test(raw)) {
    return "These dates overlap each other. Check the dates and try again.";
  }
  if (/end_date must be after start_date|start_date must not be after end_date/i.test(raw)) {
    return "The end date must be on or after the start date.";
  }
  const transition = /the leave is (\w+), and can be set (\w+)/i.exec(raw);
  if (transition) {
    const current = statusWord(transition[1].toLowerCase());
    const target = statusWord(transition[2].toLowerCase());
    return `This request has already been ${current}, so it can't be ${target} now. Refresh to see its latest status.`;
  }
  const notPending = /only a pending leave can be \w+; this one is (\w+)/i.exec(raw);
  if (notPending) {
    return `Only a pending request can be changed. This one has already been ${statusWord(
      notPending[1].toLowerCase()
    )}.`;
  }
  if (/only the employee's direct manager or an executive/i.test(raw)) {
    return "Only this coach's manager or an executive can approve or reject this leave.";
  }
  if (/employee_id must be/i.test(raw)) {
    return "You can only manage your own leave, or leave for people who report to you.";
  }
  if (/Only employees can/i.test(raw)) {
    return "Your account isn't set up as an employee, so leave isn't available. Contact your team.";
  }
  if (/Leaves not found or deleted/i.test(raw)) {
    return "This leave request no longer exists. Refresh to see the latest list.";
  }
  if (/Leave types not found/i.test(raw)) {
    return "That leave type is no longer available. Close the form and choose another.";
  }
  if (/Leave statuses not found|is not configured/i.test(raw)) {
    return "Leave isn't fully set up yet. Contact your team.";
  }
  if (status === 401) return "Your session has expired. Log in again.";
  if (status === 403) return "You don't have permission to do this.";
  return FALLBACKS[action] || FALLBACKS.apply;
}
